import Button from "@components/Button"
import { useUser } from "@features/user"
import clsx from "clsx"
import useEnv from "hooks/useEnv"
import { type FC, useRef } from "react"
import { Trans } from "react-i18next"

import { Currency, getCurrencySymbol } from "../../../helpers/formatCurrencyBRL"
import type { Offer } from "../../offersList/offersListTypes"
import useDownloadImage from "../hooks/useDownloadImage"
import usePostGeaState, {
  LogoBackground,
  LogoSide,
  PriceSide,
} from "../hooks/usePostGeaState"
import GeaControls from "./GeaControls"

import styles from "./PostsGea.module.css"

interface Props {
  offer: Offer
}
const PostsGea: FC<Props> = ({ offer }) => {
  const { geconturUrl } = useEnv()
  const { data } = useUser()

  const domEl = useRef<HTMLDivElement | null>(null)

  const state = usePostGeaState()
  const downloadImage = useDownloadImage()

  if (!offer)
    return (
      <div>
        <Trans i18nKey="offersPost.notFound.title" />
      </div>
    )

  const {
    AccommodationName,
    OfferTitle,
    LeadInPrice,
    PriceType,
    ImageURL,
    Nights,
  } = offer

  const color = data?.agency?.color ?? "#4fab67"
  const logo = data?.agency?.logo_path
    ? `${geconturUrl}/img/agencies/${data?.agency?.logo_path}`
    : "https://placehold.co/150x150/jpg?text=Logo"

  return (
    <main className={`px-4 ${state.variant}`}>
      <div className="mx-auto grid justify-center px-4">
        <div className={styles.FrameWrapper}>
          <div className={styles.Frame}>
            <div ref={domEl}>
              <div className={`${styles.Root}`}>
                <img
                  className={clsx(
                    state.logoSide === LogoSide.Left
                      ? styles.LeftLogo
                      : styles.RightLogo,
                    state.logoBackground === LogoBackground.With
                      ? styles.WhiteBackground
                      : undefined,
                  )}
                  alt={OfferTitle}
                  src={logo}
                />
                <img
                  className={styles.CoverImage}
                  alt={OfferTitle}
                  src={ImageURL}
                />

                <div
                  className={
                    state.priceSide === PriceSide.Left
                      ? styles.LeftContent
                      : styles.RightContent
                  }
                >
                  <div
                    className={styles.Title}
                    style={{ background: color, color: state.titleColor }}
                    contentEditable="true"
                  >
                    {OfferTitle}
                  </div>
                  <div className={styles.Body}>
                    <div
                      className={styles.AccommodationName}
                      contentEditable="true"
                    >
                      {AccommodationName}
                    </div>

                    <div className={styles.Nights} contentEditable="true">
                      {Nights && (
                        <Trans
                          i18nKey="offersPdf.labels.nights"
                          values={{ count: Nights }}
                        />
                      )}
                    </div>

                    <div className="flex justify-left gap-x-2 mt-6 mb-12 pt-2">
                      <div className="flex justify-center gap-x-2 pt-2">
                        <div className="flex-60 w-1/8">
                          <div className={styles.Desde}>{PriceType}</div>
                          <div className={styles.Moeda} style={{ color }}>
                            {getCurrencySymbol(Currency.EUR)}
                          </div>
                        </div>
                        <div className="flex-40 w-7/8">
                          <div className={styles.Price} style={{ color }}>
                            {LeadInPrice}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.RNAVT}>
                      <Trans i18nKey="offersPost.labels.RNAVT" />{" "}
                      {data?.agency?.RNAVT}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GeaControls state={state} />

      <div className="my-4 flex justify-center">
        <Button
          primary
          medium
          onClick={() => downloadImage(domEl, offer.id.toString())}
        >
          <Trans i18nKey="offersPost.actions.downloadImage" />
        </Button>
      </div>
    </main>
  )
}

export default PostsGea
